import React, { useState, Suspense, lazy } from 'react';
import { 
  ChevronDown, ChevronRight, Phone, PhoneIncoming, PhoneOutgoing,
  GitBranch, CheckCircle, XCircle, AlertTriangle, Search, User,
  Calendar, Clock, FileText, Home, Calculator, ArrowRight,
  HelpCircle, MessageSquare, Save, Settings, Zap, List, Edit,
  MapPin, Mail, DollarSign, Users, Check, X, Play, Box, Loader2, Globe, RefreshCw
} from 'lucide-react';

// Chargement lazy des composants 3D (lourds)
const FormulairesMapping3D = lazy(() => import('./FormulairesMapping3D'));
const TeleproMapping3DFull = lazy(() => import('./TeleproMapping3DFull'));
const WorkflowComplet3D = lazy(() => import('./WorkflowComplet3D'));
const FormulaireBuilder = lazy(() => import('./FormulaireBuilder'));

const TeleproAnimatedJourney = lazy(() => import('./TeleproAnimatedJourney'));

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : MAPPINGS FORMULAIRES
// ════════════════════════════════════════════════════════════════════════════════

const FormulairesMapping = () => {
  const [activeTab, setActiveTab] = useState('editeur');
  const [expandedNodes, setExpandedNodes] = useState({
    'root': true,
    'step1': true,
    'oui': true,
    'step2': true,
    'step3': true
  });

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const expandAll = () => {
    const all = {};
    // Expand all possible nodes
    ['root', 'step1', 'oui', 'non', 'step2', 'step2-recherche', 'step2-infos', 'step2-simulateur', 
     'step3', 'step3-statut', 'step3-rdv', 'step3-bloque', 'step3-autres', 'autre_demande', 'autre-recherche',
     'autre-actions'].forEach(id => all[id] = true);
    setExpandedNodes(all);
  };

  const collapseAll = () => {
    setExpandedNodes({ 'root': true });
  };

  const tabs = [
    { id: 'editeur', label: 'Éditeur Formulaires', icon: Edit, color: 'cyan' },
    { id: 'parcours-anime', label: 'Parcours Automatisé', icon: Play, color: 'emerald' },
    { id: 'workflow-complet', label: 'Workflow SAN-IA Complet', icon: RefreshCw, color: 'teal' },
    { id: 'telepro-3d', label: 'Télépro Complet 3D', icon: Globe, color: 'blue' },
    { id: 'vue-3d', label: 'Formulaire 3D', icon: Box, color: 'pink' },
    { id: 'appels-entrants', label: 'Appels Entrants (Arbre)', icon: PhoneIncoming, color: 'emerald' },
    { id: 'appels-entrants-horizontal', label: 'Appels Entrants (Horizontal)', icon: GitBranch, color: 'purple' },
    { id: 'appels-sortants', label: 'Appels Sortants', icon: PhoneOutgoing, color: 'teal' },
    { id: 'qualification-lead', label: 'Qualification Lead', icon: User, color: 'blue' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-rose-600/20 border border-purple-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
              <GitBranch className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Mappings Formulaires</h2>
              <p className="text-slate-400">Visualisation des flux de qualification en arborescence</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
            >
              Tout déplier
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
            >
              Tout replier
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-emerald-500/20 border border-emerald-500/50 border-b-0 text-emerald-400'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'editeur' && (
        <Suspense fallback={
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center" style={{ height: '700px' }}>
            <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mb-4" />
            <p className="text-slate-400">Chargement de l'éditeur de formulaires...</p>
          </div>
        }>
          <FormulaireBuilder />
        </Suspense>
      )}

      {activeTab === 'workflow-complet' && (
        <Suspense fallback={
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center" style={{ height: '700px' }}>
            <Loader2 className="w-12 h-12 text-teal-500 animate-spin mb-4" />
            <p className="text-slate-400">Chargement du Workflow SAN-IA Complet...</p>
          </div>
        }>
          <WorkflowComplet3D />
        </Suspense>
      )}

      {activeTab === 'parcours-anime' && (
        <Suspense fallback={
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center" style={{ height: '700px' }}>
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
            <p className="text-slate-400">Chargement du parcours automatisé...</p>
          </div>
        }>
          <TeleproAnimatedJourney />
        </Suspense>
      )}

      {activeTab === 'telepro-3d' && (
        <Suspense fallback={
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center" style={{ height: '700px' }}>
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
            <p className="text-slate-400">Chargement de la carte Télépro 3D...</p>
          </div>
        }>
          <TeleproMapping3DFull />
        </Suspense>
      )}

      {activeTab === 'vue-3d' && (
        <Suspense fallback={
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center" style={{ height: '600px' }}>
            <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
            <p className="text-slate-400">Chargement de la vue 3D...</p>
          </div>
        }>
          <FormulairesMapping3D />
        </Suspense>
      )}

      {activeTab === 'appels-entrants' && (
        <AppelsEntrantsTree 
          expandedNodes={expandedNodes} 
          toggleNode={toggleNode} 
        />
      )}

      {activeTab === 'appels-entrants-horizontal' && (
        <AppelsEntrantsHorizontal />
      )}

      {activeTab === 'appels-sortants' && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
          <PhoneOutgoing className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">Mapping des appels sortants à venir...</p>
        </div>
      )}

      {activeTab === 'qualification-lead' && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
          <User className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">Mapping de qualification lead à venir...</p>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// ARBRE : APPELS ENTRANTS - PARCOURS COMPLET
// ════════════════════════════════════════════════════════════════════════════════

const AppelsEntrantsTree = ({ expandedNodes, toggleNode }) => {
  return (
    <div className="bg-slate-900/50 border border-slate-700 rounded-2xl overflow-hidden">
      {/* Légende */}
      <div className="p-4 bg-slate-800/50 border-b border-slate-700 flex flex-wrap items-center gap-4">
        <span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Légende :</span>
        <LegendItem icon={HelpCircle} color="blue" label="Question" />
        <LegendItem icon={CheckCircle} color="emerald" label="Réponse OUI" />
        <LegendItem icon={XCircle} color="red" label="Réponse NON" />
        <LegendItem icon={Play} color="teal" label="Étape" />
        <LegendItem icon={Edit} color="purple" label="Saisie" />
        <LegendItem icon={ArrowRight} color="amber" label="Action" />
        <LegendItem icon={Calendar} color="pink" label="RDV" />
      </div>

      {/* Arbre */}
      <div className="p-6 font-mono text-sm">
        {/* ROOT */}
        <TreeNode
          id="root"
          label="FORMULAIRE APPEL ENTRANT"
          sublabel="QualificationAppel.jsx"
          icon={PhoneIncoming}
          color="emerald"
          expanded={expandedNodes['root']}
          onToggle={() => toggleNode('root')}
          depth={0}
          isRoot
        >
          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* ÉTAPE 1 : QUESTION INITIALE */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          <TreeNode
            id="step1"
            label="ÉTAPE 1 : Question initiale"
            icon={HelpCircle}
            color="blue"
            expanded={expandedNodes['step1']}
            onToggle={() => toggleNode('step1')}
            depth={1}
          >
            <QuestionNode 
              question="L'appel concerne-t-il l'adaptation du logement ?"
              hint="MaPrimeAdapt', aides ANAH, travaux d'accessibilité..."
            />

            {/* ════════════════════════════════════════════════════════════════ */}
            {/* BRANCHE OUI → Parcours complet jusqu'au RDV */}
            {/* ════════════════════════════════════════════════════════════════ */}
            <TreeNode
              id="oui"
              label="OUI"
              sublabel="→ Continuer vers le test d'éligibilité"
              icon={CheckCircle}
              color="emerald"
              expanded={expandedNodes['oui']}
              onToggle={() => toggleNode('oui')}
              depth={2}
            >
              {/* ══════════════════════════════════════════════════════════════ */}
              {/* ÉTAPE 2 : SIMULATION MPA */}
              {/* ══════════════════════════════════════════════════════════════ */}
              <TreeNode
                id="step2"
                label="ÉTAPE 2 : Simulation MPA"
                sublabel="Test d'éligibilité MaPrimeAdapt'"
                icon={Calculator}
                color="teal"
                expanded={expandedNodes['step2']}
                onToggle={() => toggleNode('step2')}
                depth={3}
              >
                {/* Recherche client */}
                <TreeNode
                  id="step2-recherche"
                  label="Recherche client existant"
                  icon={Search}
                  color="blue"
                  expanded={expandedNodes['step2-recherche']}
                  onToggle={() => toggleNode('step2-recherche')}
                  depth={4}
                >
                  <FieldNode label="Champ de recherche" hint="Téléphone, nom, prénom ou email" />
                  <TreeNode
                    id="step2-recherche-trouve"
                    label="Client trouvé ?"
                    icon={HelpCircle}
                    color="blue"
                    depth={5}
                  >
                    <ActionNode label="OUI → Ouvrir fiche existante" color="emerald" />
                    <ActionNode label="OUI → Sélectionner et pré-remplir" color="emerald" />
                    <ActionNode label="NON → Nouveau client" color="amber" />
                  </TreeNode>
                </TreeNode>

                {/* Infos client */}
                <TreeNode
                  id="step2-infos"
                  label="Informations client"
                  sublabel="Minimum requis pour créer le lead"
                  icon={User}
                  color="purple"
                  expanded={expandedNodes['step2-infos']}
                  onToggle={() => toggleNode('step2-infos')}
                  depth={4}
                >
                  <FieldNode label="Nom *" hint="Obligatoire" required />
                  <FieldNode label="Prénom" />
                  <FieldNode label="Téléphone *" hint="Obligatoire" required />
                  <FieldNode label="Email" />
                </TreeNode>

                {/* Widget Simulateur MPA */}
                <TreeNode
                  id="step2-simulateur"
                  label="Widget Simulateur MPA"
                  sublabel="SimulateurMPAWidget"
                  icon={Calculator}
                  color="emerald"
                  expanded={expandedNodes['step2-simulateur']}
                  onToggle={() => toggleNode('step2-simulateur')}
                  depth={4}
                >
                  <FieldNode label="Revenu fiscal de référence" hint="En euros" />
                  <FieldNode label="Nombre de personnes au foyer" />
                  <FieldNode label="Région" hint="Île-de-France / Autres régions" />
                  <Divider />
                  <ResultNode 
                    label="Résultat éligibilité" 
                    results={[
                      { text: "Très Modeste → 70% de prise en charge", color: "emerald" },
                      { text: "Modeste → 50% de prise en charge", color: "blue" },
                      { text: "Non éligible", color: "red" }
                    ]}
                  />
                </TreeNode>

                {/* Bouton continuer */}
                <ActionNode 
                  label="CONTINUER VERS FINALISATION →" 
                  hint="Requis: Nom et Téléphone renseignés"
                  color="amber"
                  bold
                />
              </TreeNode>

              {/* ══════════════════════════════════════════════════════════════ */}
              {/* ÉTAPE 3 : FINALISATION */}
              {/* ══════════════════════════════════════════════════════════════ */}
              <TreeNode
                id="step3"
                label="ÉTAPE 3 : Finalisation"
                sublabel="Choix du statut final du lead"
                icon={Save}
                color="teal"
                expanded={expandedNodes['step3']}
                onToggle={() => toggleNode('step3')}
                depth={3}
              >
                {/* Récapitulatif */}
                <InfoNode 
                  label="Récapitulatif client" 
                  items={["Nom + Prénom", "Téléphone", "Résultat éligibilité MPA"]}
                />

                {/* CHOIX STATUT */}
                <TreeNode
                  id="step3-statut"
                  label="Sélectionner le statut du lead"
                  icon={HelpCircle}
                  color="blue"
                  expanded={expandedNodes['step3-statut']}
                  onToggle={() => toggleNode('step3-statut')}
                  depth={4}
                >
                  {/* Option 1: RDV */}
                  <TreeNode
                    id="step3-rdv"
                    label="📅 Prendre RDV"
                    icon={Calendar}
                    color="pink"
                    expanded={expandedNodes['step3-rdv']}
                    onToggle={() => toggleNode('step3-rdv')}
                    depth={5}
                  >
                    <ChoiceNode label="RDV MPA Éligible" color="emerald" result="→ Lead créé avec statut RDV" />
                    <ChoiceNode label="RDV Hors MPA" color="emerald" result="→ Lead créé avec statut RDV" />
                    <Divider />
                    <ActionNode 
                      label="CRÉER LE LEAD →" 
                      hint="Ouvre la fiche client pour planifier le RDV"
                      color="emerald"
                      bold
                    />
                    <FinalNode label="✅ FIN : RDV PRIS - Lead créé dans le pipeline" color="emerald" />
                  </TreeNode>

                  {/* Option 2: Bloqué */}
                  <TreeNode
                    id="step3-bloque"
                    label="⏰ Bloqué / À rappeler"
                    icon={Clock}
                    color="amber"
                    expanded={expandedNodes['step3-bloque']}
                    onToggle={() => toggleNode('step3-bloque')}
                    depth={5}
                  >
                    <ChoiceNode label="Motif blocage 1" color="amber" />
                    <ChoiceNode label="Motif blocage 2" color="amber" />
                    <ChoiceNode label="..." hint="Jusqu'à 6 motifs configurables" color="amber" />
                    <FieldNode label="Date de rappel" />
                    <FieldNode label="Heure de rappel" />
                    <ActionNode label="CRÉER LE LEAD →" color="amber" bold />
                    <FinalNode label="⏰ FIN : RAPPEL PLANIFIÉ" color="amber" />
                  </TreeNode>

                  {/* Option 3: Autres produits */}
                  <TreeNode
                    id="step3-autres"
                    label="🔧 Intéressé autres produits"
                    icon={Home}
                    color="purple"
                    expanded={expandedNodes['step3-autres']}
                    onToggle={() => toggleNode('step3-autres')}
                    depth={5}
                  >
                    <ChoiceNode label="Travaux Hors Prime" color="purple" />
                    <ChoiceNode label="Réno Énergie" color="blue" />
                    <ActionNode label="CRÉER LE LEAD →" color="purple" bold />
                    <FinalNode label="🔧 FIN : LEAD AUTRES PRODUITS" color="purple" />
                  </TreeNode>
                </TreeNode>

                {/* Notes */}
                <FieldNode label="Notes (optionnel)" hint="Textarea - informations complémentaires" />
              </TreeNode>
            </TreeNode>

            {/* ════════════════════════════════════════════════════════════════ */}
            {/* BRANCHE NON → Autre demande */}
            {/* ════════════════════════════════════════════════════════════════ */}
            <TreeNode
              id="non"
              label="NON"
              sublabel="→ La demande concerne autre chose"
              icon={XCircle}
              color="red"
              expanded={expandedNodes['non']}
              onToggle={() => toggleNode('non')}
              depth={2}
            >
              <TreeNode
                id="autre_demande"
                label="ÉTAPE : Autre demande"
                icon={MessageSquare}
                color="amber"
                expanded={expandedNodes['autre_demande']}
                onToggle={() => toggleNode('autre_demande')}
                depth={3}
              >
                <TreeNode
                  id="autre-recherche"
                  label="Recherche client existant"
                  icon={Search}
                  color="blue"
                  depth={4}
                >
                  <ActionNode label="Client trouvé → Mettre à jour la fiche" color="emerald" />
                  <ActionNode label="Client non trouvé → Enregistrer dans les stats" color="amber" />
                </TreeNode>
                <FieldNode label="Description de la demande *" hint="Textarea obligatoire" required />
                <TreeNode
                  id="autre-actions"
                  label="Actions possibles"
                  icon={Zap}
                  color="purple"
                  depth={4}
                >
                  <ActionNode label="Enregistrer et fermer" color="amber" />
                  <ActionNode label="Continuer vers test d'éligibilité" hint="Si intéressé aussi par MPA" color="emerald" />
                </TreeNode>
                <FinalNode label="📋 FIN : AUTRE DEMANDE ENREGISTRÉE" color="amber" />
              </TreeNode>
            </TreeNode>
          </TreeNode>
        </TreeNode>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// VUE HORIZONTALE : APPELS ENTRANTS (FLOWCHART GAUCHE → DROITE) - VERSION OPTIMISÉE
// ════════════════════════════════════════════════════════════════════════════════

const AppelsEntrantsHorizontal = () => {
  return (
    <div className="bg-slate-900/50 border border-slate-700 rounded-2xl overflow-hidden">
      {/* Légende */}
      <div className="p-4 bg-slate-800/50 border-b border-slate-700 flex flex-wrap items-center gap-4">
        <span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Légende :</span>
        <LegendItem icon={Play} color="emerald" label="Début" />
        <LegendItem icon={HelpCircle} color="blue" label="Question/Décision" />
        <LegendItem icon={CheckCircle} color="emerald" label="OUI / Positif" />
        <LegendItem icon={XCircle} color="red" label="NON / Négatif" />
        <LegendItem icon={Edit} color="purple" label="Action/Saisie" />
        <LegendItem icon={Calendar} color="pink" label="Fin/Résultat" />
      </div>

      {/* Flowchart horizontal - scroll horizontal */}
      <div className="p-6 overflow-x-auto">
        <div className="min-w-[2800px] space-y-6">
          
          {/* ════════════════════════════════════════════════════════════════════ */}
          {/* FLUX OUI : Adaptation du logement - COMPLET */}
          {/* ════════════════════════════════════════════════════════════════════ */}
          
          <div className="mb-2">
            <div className="text-sm text-emerald-400 font-bold uppercase tracking-wider flex items-center gap-2">
              <CheckCircle size={16} /> FLUX OUI : L'appel concerne l'adaptation du logement
            </div>
          </div>

          {/* LIGNE PRINCIPALE OUI */}
          <div className="flex items-start gap-0">
            {/* Colonne 1: Début */}
            <div className="flex items-center">
              <HBox color="emerald" icon={PhoneIncoming} text="APPEL ENTRANT" />
              <HArrow />
              <HBox color="blue" icon={HelpCircle} text="Concerne adaptation logement ?" subtext="MPA, ANAH, accessibilité" />
              <HArrow label="OUI" color="emerald" />
              <HBox color="teal" icon={Search} text="RECHERCHE CLIENT" subtext="Tél, nom, prénom, email" />
              <HArrow />
              <HBox color="blue" icon={HelpCircle} text="Client trouvé ?" />
            </div>
            
            {/* Colonne 2: Branche Client trouvé + Convergence */}
            <div className="flex flex-col items-start ml-2">
              {/* Branche OUI - Client trouvé */}
              <div className="flex items-center">
                <HArrow label="OUI" color="emerald" />
                <HBox color="emerald" icon={User} text="SÉLECTIONNER" subtext="Pré-remplissage auto" />
                <HArrow />
                <HBox color="purple" icon={Edit} text="COMPLÉTER SI MANQUANT" subtext="Vérifier/corriger" />
                <HArrowDown />
              </div>
              
              {/* Point de convergence */}
              <div className="flex items-center my-2 ml-[340px]">
                <div className="w-8 h-8 rounded-full bg-teal-500/30 border-2 border-teal-500 flex items-center justify-center">
                  <ArrowRight size={16} className="text-teal-400" />
                </div>
              </div>
              
              {/* Branche NON - Nouveau client */}
              <div className="flex items-center">
                <HArrow label="NON" color="red" />
                <HBox color="amber" icon={User} text="NOUVEAU CLIENT" />
                <HArrow />
                <HBox color="purple" icon={Edit} text="SAISIR TOUS LES CHAMPS" subtext="Nom*, Prénom, Tél*, Email" />
                <HArrowUp />
              </div>
            </div>

            {/* Colonne 3: Suite après convergence - TEST ÉLIGIBILITÉ */}
            <div className="flex items-center ml-4">
              <HBox color="teal" icon={Calculator} text="TEST ÉLIGIBILITÉ MPA" subtext="Widget Simulateur" />
              <HArrow />
              <HBox color="purple" icon={Edit} text="SAISIE SIMULATEUR" subtext="Revenu, Personnes, Région" />
              <HArrow />
              <HBox color="blue" icon={HelpCircle} text="Résultat éligibilité ?" />
            </div>

            {/* Colonne 4: Résultats éligibilité */}
            <div className="flex flex-col items-start ml-2 gap-2">
              <div className="flex items-center">
                <HArrow label="Très Modeste" color="emerald" />
                <HBox color="emerald" icon={CheckCircle} text="ÉLIGIBLE 70%" />
                <HArrowRight color="teal" />
              </div>
              <div className="flex items-center">
                <HArrow label="Modeste" color="blue" />
                <HBox color="blue" icon={CheckCircle} text="ÉLIGIBLE 50%" />
                <HArrowRight color="teal" />
              </div>
              <div className="flex items-center">
                <HArrow label="Non éligible" color="red" />
                <HBox color="red" icon={XCircle} text="NON ÉLIGIBLE" />
                <HArrowRight color="amber" />
              </div>
            </div>

            {/* Colonne 5: Finalisation */}
            <div className="flex items-center ml-2">
              <HBox color="teal" icon={Save} text="FINALISATION" subtext="Choix statut final" />
              <HArrow />
              <HBox color="blue" icon={HelpCircle} text="Quel statut ?" subtext="3 options" />
            </div>

            {/* Colonne 6: Choix statut final */}
            <div className="flex flex-col items-start ml-2 gap-2">
              {/* RDV */}
              <div className="flex items-center">
                <HArrow label="RDV" color="pink" />
                <HBox color="blue" icon={HelpCircle} text="Type RDV ?" />
                <div className="flex flex-col gap-1 ml-2">
                  <div className="flex items-center">
                    <HArrow mini />
                    <HBox color="emerald" icon={Calendar} text="RDV MPA Éligible" small />
                    <HArrow />
                    <HEndBox color="emerald" text="✅ FIN: LEAD CRÉÉ" subtext="Pipeline: R1 à programmer" />
                  </div>
                  <div className="flex items-center">
                    <HArrow mini />
                    <HBox color="teal" icon={Calendar} text="RDV Hors MPA" small />
                    <HArrow />
                    <HEndBox color="teal" text="✅ FIN: LEAD CRÉÉ" subtext="Pipeline: Hors MPA" />
                  </div>
                </div>
              </div>
              
              {/* Bloqué */}
              <div className="flex items-center">
                <HArrow label="Bloqué" color="amber" />
                <HBox color="amber" icon={Clock} text="MOTIF BLOCAGE" subtext="Sélection motif" />
                <HArrow />
                <HBox color="purple" icon={Calendar} text="DATE + HEURE" subtext="Rappel planifié" />
                <HArrow />
                <HEndBox color="amber" text="⏰ FIN: RAPPEL" subtext="Pipeline: Lead à rappeler" />
              </div>
              
              {/* Autres produits */}
              <div className="flex items-center">
                <HArrow label="Autres" color="purple" />
                <HBox color="blue" icon={HelpCircle} text="Quel produit ?" />
                <div className="flex flex-col gap-1 ml-2">
                  <div className="flex items-center">
                    <HArrow mini />
                    <HBox color="purple" icon={Home} text="Travaux Hors Prime" small />
                    <HArrow />
                    <HEndBox color="purple" text="🔧 FIN: LEAD CRÉÉ" subtext="Produit: Hors Prime" />
                  </div>
                  <div className="flex items-center">
                    <HArrow mini />
                    <HBox color="blue" icon={Zap} text="Réno Énergie" small />
                    <HArrow />
                    <HEndBox color="blue" text="🔧 FIN: LEAD CRÉÉ" subtext="Produit: Réno Énergie" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Séparateur */}
          <div className="border-t-2 border-dashed border-slate-600 my-8" />

          {/* ════════════════════════════════════════════════════════════════════ */}
          {/* FLUX NON : Autre demande - COMPLET */}
          {/* ════════════════════════════════════════════════════════════════════ */}
          
          <div className="mb-2">
            <div className="text-sm text-red-400 font-bold uppercase tracking-wider flex items-center gap-2">
              <XCircle size={16} /> FLUX NON : La demande ne concerne PAS l'adaptation du logement
            </div>
          </div>

          {/* LIGNE PRINCIPALE NON */}
          <div className="flex items-start gap-0">
            {/* Colonne 1: Début */}
            <div className="flex items-center">
              <HBox color="emerald" icon={PhoneIncoming} text="APPEL ENTRANT" />
              <HArrow />
              <HBox color="blue" icon={HelpCircle} text="Concerne adaptation logement ?" />
              <HArrow label="NON" color="red" />
              <HBox color="amber" icon={MessageSquare} text="AUTRE DEMANDE" subtext="Hors périmètre MPA" />
              <HArrow />
              <HBox color="teal" icon={Search} text="RECHERCHE CLIENT" />
              <HArrow />
              <HBox color="blue" icon={HelpCircle} text="Client trouvé ?" />
            </div>

            {/* Colonne 2: Branches */}
            <div className="flex flex-col items-start ml-2 gap-3">
              {/* Branche OUI - Client trouvé */}
              <div className="flex items-center">
                <HArrow label="OUI" color="emerald" />
                <HBox color="emerald" icon={User} text="SÉLECTIONNER" />
                <HArrow />
                <HBox color="purple" icon={Edit} text="DESCRIPTION DEMANDE *" subtext="Textarea obligatoire" />
                <HArrow />
                <HBox color="blue" icon={HelpCircle} text="Aussi intéressé MPA ?" />
                <div className="flex flex-col gap-1 ml-2">
                  <div className="flex items-center">
                    <HArrow label="OUI" color="emerald" mini />
                    <HBox color="teal" icon={ArrowRight} text="→ RETOUR FLUX OUI" subtext="Vers Test Éligibilité" small />
                  </div>
                  <div className="flex items-center">
                    <HArrow label="NON" color="red" mini />
                    <HEndBox color="amber" text="📋 FIN: ENREGISTRÉ" subtext="Autre demande sur fiche" />
                  </div>
                </div>
              </div>
              
              {/* Branche NON - Client non trouvé */}
              <div className="flex items-center">
                <HArrow label="NON" color="red" />
                <HBox color="amber" icon={User} text="PAS DE FICHE" subtext="Stats uniquement" />
                <HArrow />
                <HBox color="purple" icon={Edit} text="DESCRIPTION DEMANDE *" subtext="Textarea obligatoire" />
                <HArrow />
                <HEndBox color="amber" text="📋 FIN: STATS ENREGISTRÉES" subtext="Compteur autre demande +1" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANTS FLOWCHART HORIZONTAL (Gauche → Droite)
// ════════════════════════════════════════════════════════════════════════════════

const HBox = ({ color, icon: Icon, text, subtext, small }) => {
  const colors = {
    emerald: { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.5)', text: '#34d399' },
    teal: { bg: 'rgba(20, 184, 166, 0.15)', border: 'rgba(20, 184, 166, 0.5)', text: '#2dd4bf' },
    blue: { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.5)', text: '#60a5fa' },
    amber: { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.5)', text: '#fbbf24' },
    red: { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.5)', text: '#f87171' },
    purple: { bg: 'rgba(168, 85, 247, 0.15)', border: 'rgba(168, 85, 247, 0.5)', text: '#a855f7' },
    pink: { bg: 'rgba(236, 72, 153, 0.15)', border: 'rgba(236, 72, 153, 0.5)', text: '#f472b6' },
    slate: { bg: 'rgba(100, 116, 139, 0.15)', border: 'rgba(100, 116, 139, 0.5)', text: '#94a3b8' },
  };
  const c = colors[color] || colors.teal;
  
  return (
    <div 
      className={`rounded-lg border-2 flex items-center gap-2 whitespace-nowrap
        ${small ? 'px-2 py-1' : 'px-3 py-2'}`}
      style={{ backgroundColor: c.bg, borderColor: c.border }}
    >
      <Icon size={small ? 14 : 18} style={{ color: c.text }} />
      <div className="flex flex-col">
        <span className={`font-bold ${small ? 'text-xs' : 'text-sm'}`} style={{ color: c.text }}>
          {text}
        </span>
        {subtext && <span className="text-xs text-slate-500">{subtext}</span>}
      </div>
    </div>
  );
};

const HArrow = ({ label, color = 'slate', mini, dashed }) => {
  const colors = {
    emerald: '#34d399',
    red: '#f87171',
    blue: '#60a5fa',
    amber: '#fbbf24',
    purple: '#a855f7',
    pink: '#f472b6',
    teal: '#2dd4bf',
    slate: '#64748b',
  };
  const c = colors[color] || colors.slate;
  
  return (
    <div className="flex items-center gap-1 mx-1">
      {label && (
        <span className="text-xs font-bold px-1.5 py-0.5 rounded" 
          style={{ backgroundColor: `${c}20`, color: c }}>
          {label}
        </span>
      )}
      <div className={`flex items-center ${mini ? 'w-4' : 'w-8'}`}>
        <div className={`flex-1 border-t-2 ${dashed ? 'border-dashed' : ''}`} 
          style={{ borderColor: c }} />
        <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[6px] border-transparent" 
          style={{ borderLeftColor: c }} />
      </div>
    </div>
  );
};

const HArrowRight = ({ color = 'slate' }) => {
  const c = color === 'teal' ? '#2dd4bf' : color === 'amber' ? '#fbbf24' : '#64748b';
  return (
    <div className="flex items-center w-6 mx-1">
      <div className="flex-1 border-t-2 border-dashed" style={{ borderColor: c }} />
      <div className="w-0 h-0 border-t-[4px] border-b-[4px] border-l-[5px] border-transparent" 
        style={{ borderLeftColor: c }} />
    </div>
  );
};

const HArrowDown = () => (
  <div className="flex flex-col items-center mx-2">
    <div className="h-4 border-l-2 border-teal-500" />
    <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-transparent border-t-teal-500" />
  </div>
);

const HArrowUp = () => (
  <div className="flex flex-col items-center mx-2">
    <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[6px] border-transparent border-b-teal-500" />
    <div className="h-4 border-l-2 border-teal-500" />
  </div>
);

const HEndBox = ({ color, text, subtext }) => {
  const colors = {
    emerald: { bg: 'rgba(16, 185, 129, 0.25)', border: 'rgba(16, 185, 129, 0.7)', text: '#34d399' },
    amber: { bg: 'rgba(245, 158, 11, 0.25)', border: 'rgba(245, 158, 11, 0.7)', text: '#fbbf24' },
    purple: { bg: 'rgba(168, 85, 247, 0.25)', border: 'rgba(168, 85, 247, 0.7)', text: '#a855f7' },
    blue: { bg: 'rgba(59, 130, 246, 0.25)', border: 'rgba(59, 130, 246, 0.7)', text: '#60a5fa' },
    teal: { bg: 'rgba(20, 184, 166, 0.25)', border: 'rgba(20, 184, 166, 0.7)', text: '#2dd4bf' },
    red: { bg: 'rgba(239, 68, 68, 0.25)', border: 'rgba(239, 68, 68, 0.7)', text: '#f87171' },
  };
  const c = colors[color] || colors.emerald;
  
  return (
    <div 
      className="px-4 py-2 rounded-xl border-2 flex flex-col items-center"
      style={{ backgroundColor: c.bg, borderColor: c.border }}
    >
      <span className="font-bold text-sm" style={{ color: c.text }}>{text}</span>
      {subtext && <span className="text-xs text-slate-400">{subtext}</span>}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANTS HELPERS
// ════════════════════════════════════════════════════════════════════════════════

const LegendItem = ({ icon: Icon, color, label }) => {
  const colors = {
    blue: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
    emerald: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400',
    red: 'bg-red-500/20 border-red-500/50 text-red-400',
    teal: 'bg-teal-500/20 border-teal-500/50 text-teal-400',
    purple: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
    amber: 'bg-amber-500/20 border-amber-500/50 text-amber-400',
    pink: 'bg-pink-500/20 border-pink-500/50 text-pink-400',
  };
  
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-5 h-5 rounded border ${colors[color]} flex items-center justify-center`}>
        <Icon size={12} />
      </div>
      <span className="text-xs text-slate-400">{label}</span>
    </div>
  );
};

const QuestionNode = ({ question, hint }) => (
  <div className="ml-8 my-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
    <p className="text-blue-300 font-medium">"  {question}"</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const FieldNode = ({ label, hint, required }) => (
  <div className="flex items-center gap-2 ml-8 my-1 py-1">
    <div className="w-5 h-5 rounded bg-purple-500/20 border border-purple-500/50 flex items-center justify-center">
      <Edit size={10} className="text-purple-400" />
    </div>
    <span className="text-purple-300">{label}</span>
    {required && <span className="text-xs text-red-400">*</span>}
    {hint && <span className="text-xs text-slate-500">({hint})</span>}
  </div>
);

const ActionNode = ({ label, hint, color = 'amber', bold }) => (
  <div className="flex items-center gap-2 ml-8 my-1 py-1">
    <div className={`w-5 h-5 rounded bg-${color}-500/20 border border-${color}-500/50 flex items-center justify-center`}
      style={{ 
        backgroundColor: color === 'emerald' ? 'rgba(16, 185, 129, 0.2)' : color === 'amber' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(168, 85, 247, 0.2)',
        borderColor: color === 'emerald' ? 'rgba(16, 185, 129, 0.5)' : color === 'amber' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(168, 85, 247, 0.5)'
      }}
    >
      <ArrowRight size={10} style={{ color: color === 'emerald' ? '#34d399' : color === 'amber' ? '#fbbf24' : '#a855f7' }} />
    </div>
    <span className={`${bold ? 'font-bold' : ''}`} style={{ color: color === 'emerald' ? '#34d399' : color === 'amber' ? '#fbbf24' : '#a855f7' }}>
      {label}
    </span>
    {hint && <span className="text-xs text-slate-500">({hint})</span>}
  </div>
);

const ChoiceNode = ({ label, hint, color = 'emerald', result }) => (
  <div className="flex items-center gap-2 ml-8 my-1 py-1">
    <div className="w-5 h-5 rounded flex items-center justify-center"
      style={{ 
        backgroundColor: color === 'emerald' ? 'rgba(16, 185, 129, 0.2)' : color === 'amber' ? 'rgba(245, 158, 11, 0.2)' : color === 'purple' ? 'rgba(168, 85, 247, 0.2)' : 'rgba(59, 130, 246, 0.2)',
        border: `1px solid ${color === 'emerald' ? 'rgba(16, 185, 129, 0.5)' : color === 'amber' ? 'rgba(245, 158, 11, 0.5)' : color === 'purple' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(59, 130, 246, 0.5)'}`
      }}
    >
      <Check size={10} style={{ color: color === 'emerald' ? '#34d399' : color === 'amber' ? '#fbbf24' : color === 'purple' ? '#a855f7' : '#60a5fa' }} />
    </div>
    <span className="text-white">{label}</span>
    {hint && <span className="text-xs text-slate-500">({hint})</span>}
    {result && <span className="text-xs text-slate-400 ml-2">{result}</span>}
  </div>
);

const InfoNode = ({ label, items }) => (
  <div className="ml-8 my-2 p-3 bg-slate-700/30 border border-slate-600 rounded-lg">
    <p className="text-slate-300 font-medium flex items-center gap-2">
      <FileText size={14} className="text-slate-400" />
      {label}
    </p>
    <ul className="mt-2 space-y-1">
      {items.map((item, i) => (
        <li key={i} className="text-xs text-slate-400 ml-5">• {item}</li>
      ))}
    </ul>
  </div>
);

const ResultNode = ({ label, results }) => (
  <div className="ml-8 my-2 p-3 bg-slate-800/50 border border-slate-600 rounded-lg">
    <p className="text-slate-300 font-medium mb-2">{label}</p>
    {results.map((r, i) => (
      <p key={i} className="text-xs ml-4" style={{ color: r.color === 'emerald' ? '#34d399' : r.color === 'blue' ? '#60a5fa' : '#f87171' }}>
        • {r.text}
      </p>
    ))}
  </div>
);

const FinalNode = ({ label, color }) => (
  <div className="ml-8 my-3 p-3 rounded-lg font-bold text-center"
    style={{ 
      backgroundColor: color === 'emerald' ? 'rgba(16, 185, 129, 0.2)' : color === 'amber' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(168, 85, 247, 0.2)',
      border: `2px solid ${color === 'emerald' ? 'rgba(16, 185, 129, 0.5)' : color === 'amber' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(168, 85, 247, 0.5)'}`,
      color: color === 'emerald' ? '#34d399' : color === 'amber' ? '#fbbf24' : '#a855f7'
    }}
  >
    {label}
  </div>
);

const Divider = () => (
  <div className="ml-8 my-2 border-t border-dashed border-slate-600" />
);

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : NOEUD D'ARBRE
// ════════════════════════════════════════════════════════════════════════════════

const TreeNode = ({ 
  id, 
  label, 
  sublabel,
  icon: Icon, 
  color = 'slate', 
  expanded, 
  onToggle, 
  depth = 0, 
  children,
  isRoot
}) => {
  const hasChildren = React.Children.count(children) > 0;
  const indent = depth * 28;
  
  const colorStyles = {
    emerald: { bg: 'rgba(16, 185, 129, 0.2)', border: 'rgba(16, 185, 129, 0.5)', text: '#34d399' },
    teal: { bg: 'rgba(20, 184, 166, 0.2)', border: 'rgba(20, 184, 166, 0.5)', text: '#2dd4bf' },
    blue: { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.5)', text: '#60a5fa' },
    amber: { bg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 0.5)', text: '#fbbf24' },
    red: { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.5)', text: '#f87171' },
    purple: { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.5)', text: '#a855f7' },
    pink: { bg: 'rgba(236, 72, 153, 0.2)', border: 'rgba(236, 72, 153, 0.5)', text: '#f472b6' },
    slate: { bg: 'rgba(100, 116, 139, 0.2)', border: 'rgba(100, 116, 139, 0.5)', text: '#94a3b8' },
  };
  
  const colors = colorStyles[color] || colorStyles.slate;

  return (
    <div className="relative">
      {/* Ligne verticale */}
      {depth > 0 && (
        <div 
          className="absolute top-0 bottom-0 border-l-2 border-slate-700"
          style={{ left: indent - 14 }}
        />
      )}
      
      {/* Noeud */}
      <div 
        className={`flex items-start gap-3 py-2 px-2 rounded-lg transition-colors ${hasChildren ? 'cursor-pointer hover:bg-slate-800/50' : ''} ${isRoot ? 'bg-slate-800/30 mb-2' : ''}`}
        style={{ marginLeft: indent }}
        onClick={() => hasChildren && onToggle?.()}
      >
        {/* Chevron */}
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
          )}
        </div>
        
        {/* Icône */}
        <div 
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
        >
          <Icon size={16} style={{ color: colors.text }} />
        </div>
        
        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span 
              className={`font-semibold ${isRoot ? 'text-lg' : ''}`}
              style={{ color: isRoot ? 'white' : colors.text }}
            >
              {label}
            </span>
          </div>
          {sublabel && (
            <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>
          )}
        </div>
      </div>
      
      {/* Enfants */}
      {hasChildren && expanded && (
        <div className="relative">
          {children}
        </div>
      )}
    </div>
  );
};

export default FormulairesMapping;
